import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { User, Mail, Phone, MapPin, Briefcase, Save, GraduationCap, UserCog, Award, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import SignatureUploader from "@/components/SignatureUploader";

const LEVEL_COLORS = {
  p1_starter:      "bg-amber-100 text-amber-800 border-amber-300",
  p2_professional: "bg-blue-100 text-blue-800 border-blue-300",
  p3_senior:       "bg-violet-100 text-violet-800 border-violet-300",
  p4_partner:      "bg-prime/20 text-prime border-prime/40",
};
const LEVEL_LABEL = {
  p1_starter: "P1 Starter",
  p2_professional: "P2 Professional",
  p3_senior: "P3 Senior",
  p4_partner: "P4 Partner",
};

export default function KonsulentenAnagraficaPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/users/full");
      setUsers(r.data || []);
    } catch { toast.error("Errore caricamento utenti"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      full_name: u.full_name || "",
      commission_scheme: u.commission_scheme || "p2_professional",
      mentor_id: u.mentor_id || "",
      finma_registered: !!u.finma_registered,
      finma_register_nr: u.finma_register_nr || "",
      birth_date: u.birth_date || "",
      address: u.address || "",
      npa: u.npa || "",
      city: u.city || "",
      phone: u.phone || "",
      iban: u.iban || "",
      ahv_number: u.ahv_number || "",
      vbv_iaf_number: u.vbv_iaf_number || "",
      work_permit: u.work_permit || "",
      civil_status: u.civil_status || "",
      activity_start_date: u.activity_start_date || "",
    });
  };

  const save = async () => {
    if (!editing) return;
    try {
      // Clean empty mentor_id
      const payload = { ...form };
      if (!payload.mentor_id) delete payload.mentor_id;
      await api.patch(`/users/${editing.id}/anagrafica`, payload);
      toast.success("Anagrafica aggiornata");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const deleteUser = async (u, e) => {
    e.stopPropagation();
    const ok = window.confirm(
      `Eliminare DEFINITIVAMENTE l'utente ${u.full_name || u.email}?\n\nQuesta azione rimuove l'account, l'accesso al CRM e tutti i dati anagrafici. I clienti assegnati verranno dis-assegnati.`
    );
    if (!ok) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Utente eliminato");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    }
  };

  const possibleMentors = (currentLevel) => {
    // Only higher-level users can be mentors of a P1
    if (currentLevel !== "p1_starter") return [];
    return users.filter(u => ["p2_professional", "p3_senior", "p4_partner"].includes(u.commission_scheme));
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto" data-testid="anagrafica-page">
      <div className="text-xs text-prime uppercase tracking-widest">/ Direktion / Konsulenten-Anagrafica</div>

      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl text-stone-900 flex items-center gap-2">
            <UserCog className="w-7 h-7 text-prime" strokeWidth={1.6} />
            Anagrafica Consulenti
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Dati personali · Livello FINMA · Mentor · IBAN per pagamenti provvigione.
          </p>
        </div>
        <Button onClick={load} disabled={loading} className="bg-prime hover:bg-prime/90 text-white" size="sm">
          {loading ? "Caricamento…" : "Aggiorna"}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="users-grid">
        {users.map((u) => {
          const isMaster = (u.role === "master" || (u.roles || []).includes("super_admin"));
          return (
          <Card key={u.id} className="hover:shadow-md transition cursor-pointer relative group" onClick={() => openEdit(u)} data-testid={`user-card-${u.id}`}>
            <CardContent className="p-4 space-y-2">
              {!isMaster && (
                <button
                  onClick={(e) => deleteUser(u, e)}
                  className="absolute top-2 right-2 p-1.5 rounded-md text-stone-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition"
                  title="Elimina utente"
                  data-testid={`delete-user-${u.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-prime/15 text-prime flex items-center justify-center font-display font-semibold text-base shrink-0">
                    {(u.full_name || u.email || "?").substring(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-medium truncate">{u.full_name || u.email}</h3>
                    <p className="text-[11px] text-muted-foreground truncate">{u.email}</p>
                  </div>
                </div>
                <Badge variant="outline" className={`text-[10px] shrink-0 ${LEVEL_COLORS[u.commission_scheme] || ""}`}>
                  {LEVEL_LABEL[u.commission_scheme] || u.commission_scheme}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-1 text-[11px] text-muted-foreground pt-2 border-t border-stone-100">
                <div className="flex items-center gap-1">
                  <Award className="w-3 h-3" />
                  FINMA: {u.finma_registered ? <b className="text-emerald-600">✓</b> : <b className="text-amber-600">No</b>}
                </div>
                <div className="flex items-center gap-1">
                  <GraduationCap className="w-3 h-3" />
                  {u._mentor ? <span title={`Coach: ${u._mentor.full_name}`} className="truncate">Coach: {u._mentor.full_name}</span> : <span className="text-stone-400">no coach</span>}
                </div>
                <div className="flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  <span className="truncate">{u.phone || "—"}</span>
                </div>
                <div className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  <span className="truncate">{u.city || "—"}</span>
                </div>
              </div>
            </CardContent>
          </Card>
          );
        })}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-prime" />
              Modifica anagrafica · {editing?.full_name}
            </DialogTitle>
            <DialogDescription>{editing?.email}</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <FormField label="Nome completo">
              <Input value={form.full_name || ""} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            </FormField>
            <FormField label="Livello FINMA / Stufe">
              <Select value={form.commission_scheme} onValueChange={(v) => setForm({ ...form, commission_scheme: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="p1_starter">P1 Starter (no FINMA, coach obbligatorio)</SelectItem>
                  <SelectItem value="p2_professional">P2 Professional (FINMA)</SelectItem>
                  <SelectItem value="p3_senior">P3 Senior</SelectItem>
                  <SelectItem value="p4_partner">P4 Partner / Direktor</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            {form.commission_scheme === "p1_starter" && (
              <FormField label="Coach / Mentor (obbligatorio per P1)">
                <Select value={form.mentor_id || "__none__"} onValueChange={(v) => setForm({ ...form, mentor_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Seleziona coach…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Nessuno —</SelectItem>
                    {possibleMentors("p1_starter").filter(m => m.id !== editing?.id).map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.full_name} ({LEVEL_LABEL[m.commission_scheme]})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            )}
            <FormField label="FINMA Registriert">
              <Select value={form.finma_registered ? "true" : "false"} onValueChange={(v) => setForm({ ...form, finma_registered: v === "true" })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Sì</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="FINMA Registriernummer">
              <Input value={form.finma_register_nr || ""} onChange={(e) => setForm({ ...form, finma_register_nr: e.target.value })} placeholder="z.B. F01234567" />
            </FormField>
            <FormField label="VBV / IAF Zertifikat">
              <Input value={form.vbv_iaf_number || ""} onChange={(e) => setForm({ ...form, vbv_iaf_number: e.target.value })} placeholder="z.B. VBV-2024-1234" />
            </FormField>

            <div className="md:col-span-2 mt-2 mb-1 text-xs uppercase tracking-wider text-prime font-mono">Anagrafica</div>
            <FormField label="Data di nascita">
              <Input type="date" value={form.birth_date || ""} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
            </FormField>
            <FormField label="Telefono">
              <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+41 79 ..." />
            </FormField>
            <FormField label="Indirizzo">
              <Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Via, numero" />
            </FormField>
            <FormField label="NPA / Località">
              <div className="grid grid-cols-3 gap-2">
                <Input className="col-span-1" value={form.npa || ""} onChange={(e) => setForm({ ...form, npa: e.target.value })} placeholder="NPA" />
                <Input className="col-span-2" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Città" />
              </div>
            </FormField>
            <FormField label="Stato civile">
              <Select value={form.civil_status || "__none__"} onValueChange={(v) => setForm({ ...form, civil_status: v === "__none__" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  <SelectItem value="ledig">Ledig</SelectItem>
                  <SelectItem value="verheiratet">Verheiratet</SelectItem>
                  <SelectItem value="geschieden">Geschieden</SelectItem>
                  <SelectItem value="verwitwet">Verwitwet</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Permesso di lavoro">
              <Select value={form.work_permit || "__none__"} onValueChange={(v) => setForm({ ...form, work_permit: v === "__none__" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  <SelectItem value="CH">CH (cittadino svizzero)</SelectItem>
                  <SelectItem value="C">Permesso C</SelectItem>
                  <SelectItem value="B">Permesso B</SelectItem>
                  <SelectItem value="L">Permesso L</SelectItem>
                </SelectContent>
              </Select>
            </FormField>

            <div className="md:col-span-2 mt-2 mb-1 text-xs uppercase tracking-wider text-prime font-mono">Documenti & Pagamenti</div>
            <FormField label="AHV-Nummer">
              <Input value={form.ahv_number || ""} onChange={(e) => setForm({ ...form, ahv_number: e.target.value })} placeholder="756.xxxx.xxxx.xx" />
            </FormField>
            <FormField label="IBAN">
              <Input value={form.iban || ""} onChange={(e) => setForm({ ...form, iban: e.target.value })} placeholder="CH..." />
            </FormField>
            <FormField label="Inizio attività in Wertia">
              <Input type="date" value={form.activity_start_date || ""} onChange={(e) => setForm({ ...form, activity_start_date: e.target.value })} />
            </FormField>

            {/* Signature — used by Doc Designer to auto-fill signature blocks tagged
                with signerRole="berater_finma" or "berater_ohne_finma". */}
            <div className="md:col-span-2 mt-2 mb-1 text-xs uppercase tracking-wider text-prime font-mono">Unterschrift (Doc Designer)</div>
            <div className="md:col-span-2">
              <SignatureUploader
                value={form.signature_dataurl || ""}
                onChange={(dataUrl) => setForm({ ...form, signature_dataurl: dataUrl })}
              />
              <p className="text-[10px] text-stone-500 mt-1">
                PNG-Datei oder gezeichnete Unterschrift. Wird automatisch in Doc Designer-Vorlagen eingefügt, wenn dieses Konto der zugewiesene Berater oder Team-Leader ist.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
            <Button onClick={save} className="bg-prime hover:bg-prime/90 text-white gap-1.5" data-testid="save-btn">
              <Save className="w-4 h-4" /> Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-stone-600">{label}</Label>
      {children}
    </div>
  );
}
